import React, { useEffect, useState } from "react";
import SearchUser from "./SearchUser";
import UserList from "./UserList";

function Users() {
  const [state, setState] = useState({
    users: [],
    loading: false,
    query: "",
    error: "",
    hasSearched: false
  });
  const [compareState, setCompareState] = useState({
    first: "",
    second: "",
    result: null,
    loading: false,
    error: ""
  });

  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      if (!state.query) {
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: "", hasSearched: true }));

      try {
        const response = await fetch(
          `https://api.github.com/search/users?q=${encodeURIComponent(state.query)}`
        );
        const users = await response.json();

        if (!isMounted) {
          return;
        }

        if (users.items) {
          setState((prev) => ({ ...prev, users: users.items, loading: false }));
        } else {
          setState((prev) => ({
            ...prev,
            users: [],
            loading: false,
            error: "No users found for that search."
          }));
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setState((prev) => ({
          ...prev,
          users: [],
          loading: false,
          error: "Unable to load GitHub users right now."
        }));
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, [state.query]);

  const handleSearch = (query) => {
    const trimmed = query.trim();
    if (!trimmed) {
      return;
    }

    setState((prev) => ({ ...prev, query: trimmed, users: [], error: "" }));
  };

  const handleClear = () => {
    setState((prev) => ({ ...prev, users: [], query: "", error: "", hasSearched: false }));
  };

  const handleCompare = async (e) => {
    e.preventDefault();
    const first = compareState.first.trim();
    const second = compareState.second.trim();

    if (!first || !second) {
      setCompareState((prev) => ({ ...prev, error: "Enter two GitHub usernames to compare." }));
      return;
    }

    setCompareState((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const [firstResponse, secondResponse] = await Promise.all([
        fetch(`https://api.github.com/users/${first}`),
        fetch(`https://api.github.com/users/${second}`)
      ]);
      const [firstProfile, secondProfile] = await Promise.all([
        firstResponse.json(),
        secondResponse.json()
      ]);

      if (firstProfile.message || secondProfile.message) {
        throw new Error("One or both profiles could not be loaded.");
      }

      setCompareState((prev) => ({
        ...prev,
        loading: false,
        result: {
          first: firstProfile,
          second: secondProfile
        }
      }));
    } catch (error) {
      setCompareState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Comparison could not be completed."
      }));
    }
  };

  const showSkeleton = state.loading;
  const showEmptyState = !state.loading && !state.query && !state.hasSearched;
  const showErrorState = !state.loading && !!state.error;
  const showResults = !state.loading && state.query && state.users.length > 0;

  return (
    <div className="content-stack">
      <section className="hero-intro">
        <p className="eyebrow">GitHub profile discovery</p>
        <h1 className="brand-script">GITHUNT</h1>
        <span className="brand-underline" />
        <p className="hero-title-block">Find developers, explore portfolios, and connect faster.</p>
      </section>
      <section className="hero-card">
        <div>
          <p className="eyebrow">GitHub profile discovery</p>
          <h2>Search smarter and discover developer profiles.</h2>
          <p>
            Search GitHub users by username or names and explore their public profiles.
          </p>
        </div>
        <SearchUser
          onSearch={handleSearch}
          onClear={handleClear}
          showClear={state.query.length > 0 || state.users.length > 0}
          loading={state.loading}
        />
      </section>

      <section className="compare-panel">
        <div className="section-heading">
          <h2>Compare two profiles</h2>
          <p>See followers, public repos, and profile strength side by side.</p>
        </div>
        <form className="compare-form" onSubmit={handleCompare}>
          <input
            type="text"
            placeholder="First GitHub username"
            value={compareState.first || ""}
            onChange={(e) => {
              const value = e && e.target ? e.target.value : "";
              setCompareState((prev) => ({ ...prev, first: value, error: "" }));
            }}
          />
          <input
            type="text"
            placeholder="Second GitHub username"
            value={compareState.second || ""}
            onChange={(e) => {
              const value = e && e.target ? e.target.value : "";
              setCompareState((prev) => ({ ...prev, second: value, error: "" }));
            }}
          />
          <button type="submit" className="primary-btn" disabled={compareState.loading}>
            {compareState.loading ? "Comparing..." : "Compare"}
          </button>
        </form>
        {compareState.error && <p className="field-error">{compareState.error}</p>}
        {compareState.result && (
          <div className="compare-result">
            <div className="profile-card compact">
              <img className="avatar" src={compareState.result.first.avatar_url} alt="First profile" />
              <div>
                <h3>{compareState.result.first.login}</h3>
                <p>{compareState.result.first.name || "No display name"}</p>
                <p>Followers {compareState.result.first.followers}</p>
                <p>Repos {compareState.result.first.public_repos}</p>
              </div>
            </div>
            <div className="profile-card compact">
              <img className="avatar" src={compareState.result.second.avatar_url} alt="Second profile" />
              <div>
                <h3>{compareState.result.second.login}</h3>
                <p>{compareState.result.second.name || "No display name"}</p>
                <p>Followers {compareState.result.second.followers}</p>
                <p>Repos {compareState.result.second.public_repos}</p>
              </div>
            </div>
          </div>
        )}
      </section>

      {showSkeleton ? (
        <div className="skeleton-grid">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="skeleton-card" />
          ))}
        </div>
      ) : showErrorState ? (
        <div className="state-card error-state">
          <h3>We hit a snag</h3>
          <p>{state.error}</p>
        </div>
      ) : showEmptyState ? (
        <div className="state-card">
          <h3>Start your search</h3>
          <p>Enter a GitHub username to discover a profile and their public repositories.</p>
        </div>
      ) : showResults ? (
        <UserList users={state.users} />
      ) : (
        <div className="state-card">
          <h3>No matches yet</h3>
          <p>Try a different GitHub username to see results.</p>
        </div>
      )}
    </div>
  );
}

export default Users;
